import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";

// Components
import WorkerAvatar from "../UI/WorkerAvatar/WorkerAvatar";
import NotificationsCounter from "../UI/NotificationCounter/NotificationCounter";

// Icons
import {
  BellIcon,
  CheckAllIcon,
  Close,
  CommentBubbleIcon,
  Eye,
  GearIcon,
  Info,
  MessageBubbleIcon,
  RefreshIcon,
  ServiceRequestsIcon,
  ServicesIcon
} from "../../SVGS";

// Actions
import {
  closeNotificationsPanel,
  fetchNotifications,
  markAllNotificationsSeen,
  markNotificationSeen,
  viewNotification
} from "../../redux/actions/notifications.actions";
import { openNotificationService } from "./notificationOpeners";

// Constants
import * as tabsValues from "../../constants/accountSettingsTabs.constants";

// Utils
import {
  convertTimeStampToRelativeTime,
  convertTimeStampToUsDateTime
} from "../../utils/timeStampToDate";

import history from "../../history";

// Styles
import { NotificationPanelStyles } from "./notificationPanel.styles";

const DAY_IN_SECONDS = 86400;

const splitName = fullName => {
  const [firstName = "", ...rest] = (fullName || "").trim().split(" ");
  return { firstName, lastName: rest.join(" ") };
};

/* Resolve a notification into { title, lead, desc, meta } per its type. */
const describeNotification = notification => {
  const metadata = notification.metadata || {};
  const service = metadata.service || {};

  switch (notification.notification_type) {
    case "tickets":
      return {
        title: "You've been assigned to a service",
        lead: (
          <span className="badgeCircle">
            <ServicesIcon />
          </span>
        ),
        desc: <b>{service.service_type?.name || "Service"}</b>,
        meta: service.time ? convertTimeStampToUsDateTime(service.time) : null
      };

    case "service_requests": {
      const metaText = [
        service.mobility,
        service.time ? convertTimeStampToUsDateTime(service.time) : null
      ]
        .filter(Boolean)
        .join(" · ");
      return {
        title: "New service request",
        lead: (
          <span className="badgeCircle">
            <ServiceRequestsIcon />
          </span>
        ),
        desc: <b>{service.service_type?.name || "New request"}</b>,
        meta: metaText || null
      };
    }

    case "service_request_messages":
    case "service_messages": {
      const sender = metadata.message?.sender;
      const senderName = sender?.name || "Someone";
      return {
        title:
          notification.notification_type === "service_request_messages"
            ? "New message on a request"
            : "New message",
        lead: (
          <>
            <WorkerAvatar {...splitName(senderName)} size={40} />
            <span className="typeTag msg">
              <MessageBubbleIcon />
            </span>
          </>
        ),
        desc: (
          <>
            <b>{senderName}:</b> {metadata.message?.body || ""}
          </>
        ),
        meta: null
      };
    }

    case "service_comments": {
      const commenter = metadata.service_comment?.user;
      const commenterName = commenter
        ? `${commenter.first_name || ""} ${commenter.last_name || ""}`.trim()
        : "Someone";
      return {
        title: "New comment",
        lead: (
          <>
            <WorkerAvatar
              firstName={commenter?.first_name}
              lastName={commenter?.last_name}
              profileImage={commenter?.profile_image}
              size={40}
            />
            <span className="typeTag cmt">
              <CommentBubbleIcon />
            </span>
          </>
        ),
        desc: (
          <>
            <b>{commenterName}:</b> {metadata.service_comment?.comment || ""}
          </>
        ),
        meta: null
      };
    }

    default:
      return {
        title: "Notification",
        lead: (
          <span className="badgeCircle">
            <BellIcon size={18} />
          </span>
        ),
        desc: null,
        meta: null
      };
  }
};

const SeenBy = ({ viewers }) => {
  if (!viewers?.length) return null;

  const shown = viewers.slice(0, 3);
  const extra = viewers.length - shown.length;
  const names = viewers
    .map(v => `${v.first_name || ""} ${v.last_name || ""}`.trim())
    .join(", ");

  return (
    <div className="seenBy" title={`Seen by ${names}`}>
      <span className="eye">
        <Eye size={13} />
      </span>
      <span className="stack">
        {shown.map(viewer => (
          <WorkerAvatar
            key={viewer.id}
            firstName={viewer.first_name}
            lastName={viewer.last_name}
            profileImage={viewer.profile_image}
            size={20}
          />
        ))}
        {extra > 0 && <span className="more">+{extra}</span>}
      </span>
    </div>
  );
};

const NotificationItem = ({ notification, onClick }) => {
  const described = describeNotification(notification);

  return (
    <div
      className={`item ${notification.is_seen ? "" : "unseen"}`}
      onClick={() => onClick(notification)}
    >
      <div className="lead">{described.lead}</div>
      <div className="itemBody">
        <div className="line1">
          <span className="itemTitle">{described.title}</span>
          <span
            className="time"
            title={convertTimeStampToUsDateTime(notification.created_at)}
          >
            {convertTimeStampToRelativeTime(notification.created_at)}
          </span>
        </div>
        {described.desc && <div className="desc">{described.desc}</div>}
        {described.meta && <div className="meta">{described.meta}</div>}
        <SeenBy viewers={notification.viewers} />
      </div>
      <span className="dot" />
    </div>
  );
};

const Skeleton = () => (
  <div>
    {[0, 1, 2, 3].map(i => (
      <div className="skRow" key={i}>
        <div className="sk skAv" />
        <div className="skCol">
          <div className="sk skL1" />
          <div className="sk skL2" />
          <div className="sk skL3" />
        </div>
      </div>
    ))}
  </div>
);

const EmptyState = ({ branchName }) => (
  <div className="stateWrap">
    <div className="stateIcon">
      <BellIcon size={26} />
    </div>
    <p className="stateTitle">You're all caught up</p>
    <p className="stateSub">
      New activity at {branchName || "your branch"} will show up here.
    </p>
  </div>
);

const ErrorState = ({ onRetry }) => (
  <div className="stateWrap isError">
    <div className="stateIcon">
      <Info />
    </div>
    <p className="stateTitle">Couldn't load notifications</p>
    <p className="stateSub">
      Something went wrong on our end. Your connection looks fine — give it
      another try.
    </p>
    <button type="button" className="retryBtn" onClick={onRetry}>
      <RefreshIcon /> Try again
    </button>
  </div>
);

const NotificationPanel = () => {
  const dispatch = useDispatch();
  const { items, hasMore, loading, loadingMore, error, unreadCount } =
    useSelector(state => state.notifications);
  const { activeBranch } = useSelector(state => state.app);

  const close = () => dispatch(closeNotificationsPanel());

  const loadMore = () => {
    if (loadingMore) return;
    const last = items[items.length - 1];
    if (last) dispatch(fetchNotifications({ from: last.id }));
  };

  const onItemClick = notification => {
    if (!notification.is_seen) {
      // Marking seen also records the caller as a viewer server-side.
      dispatch(markNotificationSeen(notification.id));
    } else {
      dispatch(viewNotification(notification.id));
    }
    close();

    // Opens the detail drawer in place over the current page; on unknown
    // type / malformed metadata it's a no-op and the panel just closes.
    openNotificationService(dispatch, notification);
  };

  const openSettings = () => {
    close();
    history.push({
      pathname: "/account-settings",
      state: { tab: tabsValues.NOTIFICATIONS }
    });
  };

  const isInitialLoading = loading && items.length === 0;
  // Hold the skeleton for at least 1s — a fast fetch otherwise just flickers.
  const [showSkeleton, setShowSkeleton] = useState(false);
  const skeletonStartRef = useRef(0);
  useEffect(() => {
    if (isInitialLoading) {
      skeletonStartRef.current = Date.now();
      setShowSkeleton(true);
      return;
    }
    if (!showSkeleton) return;
    const remaining = Math.max(0, 1000 - (Date.now() - skeletonStartRef.current));
    const timer = setTimeout(() => setShowSkeleton(false), remaining);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialLoading]);

  const showError = error && items.length === 0;
  const showEmpty = !loading && !error && items.length === 0;

  const now = Math.floor(Date.now() / 1000);
  const recent = items.filter(n => now - n.created_at < DAY_IN_SECONDS);
  const earlier = items.filter(n => now - n.created_at >= DAY_IN_SECONDS);

  return (
    <NotificationPanelStyles>
      <div className="panelOverlay" onClick={close} />
      <div className="panel">
        <div className="panelHead">
          <span className="panelTitle">Notifications</span>
          <NotificationsCounter counter={unreadCount} />
          <span className="spacer" />
          <button
            type="button"
            className="linkBtn"
            disabled={unreadCount === 0}
            onClick={() => dispatch(markAllNotificationsSeen())}
          >
            <CheckAllIcon /> Mark all as seen
          </button>
          <button
            type="button"
            className="iconBtn"
            aria-label="Close"
            onClick={close}
          >
            <Close />
          </button>
        </div>

        <div className="panelBody" id="notificationsPanelBody">
          {showSkeleton && <Skeleton />}
          {!showSkeleton && showError && (
            <ErrorState onRetry={() => dispatch(fetchNotifications())} />
          )}
          {!showSkeleton && showEmpty && (
            <EmptyState branchName={activeBranch?.name} />
          )}
          {!showSkeleton && items.length > 0 && (
            <InfiniteScroll
              dataLength={items.length}
              next={loadMore}
              hasMore={hasMore}
              loader={<div className="endRow">Loading…</div>}
              scrollableTarget="notificationsPanelBody"
            >
              {recent.length > 0 && <div className="listSub">New &amp; recent</div>}
              {recent.map(notification => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={onItemClick}
                />
              ))}
              {earlier.length > 0 && <div className="listSub">Earlier</div>}
              {earlier.map(notification => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={onItemClick}
                />
              ))}
              {!hasMore && (
                <div className="endRow">
                  You've reached the end · no more notifications
                </div>
              )}
            </InfiniteScroll>
          )}
        </div>

        <div className="panelFoot">
          <button type="button" className="linkBtn" onClick={openSettings}>
            <GearIcon /> Notification settings
          </button>
        </div>
      </div>
    </NotificationPanelStyles>
  );
};

export default NotificationPanel;
